import { AudienceList } from 'hooks/queries/externalIntegrations/useMailchimpFetchAuidence'
import Styles from '../EmailBlock.module.scss'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Menu } from 'components/Menu/Menu'
import { Item, Section } from '@react-stately/collections'
import { Button } from 'blaze-ui/Button/Button'
import { Key } from '@react-types/shared'
import { Counter as BlazeCounter } from 'blaze-ui/Counter/Counter'
import { Spinner } from 'common/Spinner/Spinner'

type SendToInputProps = {
  audienceLoading: boolean
  audienceError: Error | null
  audienceLists: AudienceList[]
  selectedContacts: AudienceList | null
  onContactsChange: (contacts: AudienceList) => void
  'data-testid'?: string
}

export function SendToInput({
  audienceLoading,
  audienceError,
  audienceLists,
  selectedContacts,
  onContactsChange,
  'data-testid': testId,
}: SendToInputProps) {
  const handleSelect = (key: Key) => {
    const selected = audienceLists.find((aud) => aud.id.toString() === key.toString())
    if (selected) {
      onContactsChange(selected)
    }
  }

  if (audienceError) {
    return <span data-testid="audience-error">{audienceError.message}</span>
  }

  const hasNoAudienceLists = !audienceLoading && !audienceError && audienceLists.length === 0

  return (
    <div className={Styles.formGroup}>
      <label className={Styles.label} htmlFor="sendTo">
        Send to <span className={Styles.requiredStar}>*</span>
      </label>
      <MenuTrigger popoverClassName={Styles.popover}>
        <Button
          variant="secondary"
          withChevron="down"
          size="lg"
          fullWidth={true}
          className={Styles.selectWrapper}
          isDisabled={audienceLoading || !!audienceError || hasNoAudienceLists}
          data-testid={testId}
        >
          {audienceLoading ? (
            <div className={Styles.loadingContainer}>
              <Spinner className={Styles.spinner} />
              <span className={Styles.buttonText}>Loading audiences...</span>
            </div>
          ) : (
            <div className={Styles.buttonContent}>
              <span className={Styles.buttonText}>{selectedContacts?.label || 'Select Audience'}</span>
              {selectedContacts && (
                <span className={Styles.recipientCount}>
                  <span className={Styles.recipientLabel}>Recipients</span>
                  <BlazeCounter count={selectedContacts.memberCount} />
                </span>
              )}
            </div>
          )}
        </Button>
        <Menu
          onAction={handleSelect}
          aria-label="Audience"
          showCheckmark={false}
          selectionMode="single"
          selectedKeys={[selectedContacts?.id?.toString() as Key]}
          itemClassName={Styles.audienceItem}
        >
          <Section>
            {audienceLists.map((aud) => (
              <Item key={aud.id} textValue={aud.label}>
                <span className={Styles.name}>{aud.label}</span>
              </Item>
            ))}
          </Section>
        </Menu>
      </MenuTrigger>

      {hasNoAudienceLists && (
        <div className={Styles.errorMessage}>Please create an Audience list in Mailchimp before continuing.</div>
      )}
    </div>
  )
}
